import React, { useState, useEffect } from "react";
import axios from "axios";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Store, Truck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterprisePayouts = () => {
    const [data, setData] = useState({ sellers: [], drivers: [] });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("sellers");

    const token = localStorage.getItem('enterprise_token');

    useEffect(() => {
        fetchPayouts();
    }, []);

    const fetchPayouts = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.get(`${API}/marketplace/admin/payout-reports`, { headers });
            if (res.data.success) {
                setData(res.data);
            }
        } catch (error) {
            console.error(error);
            toast.error("Pa kapab jwenn rapò peman yo.");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-HT').format(amount || 0) + ' HTG';
    };

    const [payoutInProgress, setPayoutInProgress] = useState({});

    const handlePayout = async (id, type, amount, name) => {
        if (!window.confirm(`Èske ou sèten ou vle transfere ${formatCurrency(amount)} bay ${name} sou MonCash li?`)) {
            return;
        }

        setPayoutInProgress(prev => ({ ...prev, [id]: true }));
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const endpoint = `${API}/marketplace/admin/${type}/${id}/payout`;
            const payload = { amount_htg: amount };
            
            const res = await axios.post(endpoint, payload, { headers });
            
            if (res.data.success) {
                toast.success(res.data.message || "Peman an fèt avèk siksè!");
                fetchPayouts();
            } else {
                toast.error(res.data.error || "Peman an echwe.");
            }
        } catch (error) {
            console.error(error);
            const errMsg = error.response?.data?.detail || "Erè nan koneksyon ak sèvè a.";
            toast.error(errMsg);
        } finally {
            setPayoutInProgress(prev => ({ ...prev, [id]: false }));
        }
    };

    const sellersToPay = data.sellers?.filter(s => s.available_balance > 0) || [];
    const driversToPay = data.drivers?.filter(d => d.available_balance > 0) || [];
    const totalSellerPayout = sellersToPay.reduce((acc, curr) => acc + curr.available_balance, 0);
    const totalDriverPayout = driversToPay.reduce((acc, curr) => acc + curr.available_balance, 0);

    return (
        <EnterpriseLayout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-[#232F3E] uppercase italic tracking-tighter">Rapò Peman</h1>
                        <p className="text-gray-500 font-bold uppercase tracking-widest text-xs mt-1">Siveye balans tout machann ak chofè yo pou w fè peman rapid.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center p-24">
                        <Loader2 className="w-12 h-12 animate-spin text-[#FF9900]" />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Card className="border-0 shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={() => setActiveTab("sellers")}>
                                <CardContent className={`p-6 rounded-xl border-4 ${activeTab === "sellers" ? "border-[#FF9900]" : "border-transparent"}`}>
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-sm text-gray-500 font-bold uppercase tracking-widest">Dèt pou Machann Yo</p>
                                            <p className="text-4xl font-black italic tracking-tighter text-gray-900 mt-2">{formatCurrency(totalSellerPayout)}</p>
                                            <p className="text-xs text-gray-400 mt-1 uppercase font-bold">{sellersToPay.length} Machann pou peye</p>
                                        </div>
                                        <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                                            <Store className="w-7 h-7 text-[#FF9900]" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-0 shadow-lg cursor-pointer hover:scale-[1.02] transition-transform" onClick={() => setActiveTab("drivers")}>
                                <CardContent className={`p-6 rounded-xl border-4 ${activeTab === "drivers" ? "border-green-500" : "border-transparent"}`}>
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-sm text-gray-500 font-bold uppercase tracking-widest">Dèt pou Chofè Yo</p>
                                            <p className="text-4xl font-black italic tracking-tighter text-gray-900 mt-2">{formatCurrency(totalDriverPayout)}</p>
                                            <p className="text-xs text-gray-400 mt-1 uppercase font-bold">{driversToPay.length} Chofè pou peye</p>
                                        </div>
                                        <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
                                            <Truck className="w-7 h-7 text-green-600" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <Card className="border-0 shadow-lg mt-6">
                            <CardHeader className="bg-gray-50/50 p-6 border-b border-gray-100">
                                <CardTitle className="text-xl font-black uppercase italic tracking-tighter flex items-center gap-2">
                                    {activeTab === "sellers" ? (
                                        <><Store className="w-6 h-6 text-[#FF9900]" /> Detay Peman Machann yo</>
                                    ) : (
                                        <><Truck className="w-6 h-6 text-green-600" /> Detay Peman Chofè yo</>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-white text-gray-500 text-[10px] uppercase font-black tracking-widest border-b border-gray-100">
                                                <th className="px-6 py-4">Non</th>
                                                {activeTab === "sellers" && <th className="px-6 py-4">Magazen</th>}
                                                <th className="px-6 py-4">Kontak</th>
                                                <th className="px-6 py-4 text-right">Balans Disponib</th>
                                                                                            <th className="px-6 py-4 text-right">Aksyon</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {(activeTab === "sellers" ? data.sellers : data.drivers).filter(x => x.available_balance > 0).map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                                    <td className="px-6 py-6 font-bold text-gray-900 text-lg uppercase italic tracking-tighter">{row.name}</td>
                                                    {activeTab === "sellers" && <td className="px-6 py-6 font-medium text-gray-600">{row.store_name}</td>}
                                                    <td className="px-6 py-6 font-bold text-gray-500">{row.phone}</td>
                                                    <td className="px-6 py-6 text-right">
                                                        <span className={`text-xl font-black italic tracking-tighter ${activeTab === 'sellers' ? 'text-[#FF9900]' : 'text-green-600'}`}>
                                                            {formatCurrency(row.available_balance)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-6 text-right">
                                                        <Button
                                                            size="sm"
                                                            className={`font-bold uppercase tracking-wider text-xs rounded-xl shadow-md ${activeTab === 'sellers' ? 'bg-[#FF9900] hover:bg-[#e08800]' : 'bg-green-600 hover:bg-green-700'} text-white`}
                                                            onClick={() => handlePayout(row.id, activeTab, row.available_balance, row.name)}
                                                            disabled={payoutInProgress[row.id]}
                                                        >
                                                            {payoutInProgress[row.id] ? (
                                                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                                            ) : null}
                                                            Peye
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(activeTab === "sellers" ? data.sellers : data.drivers).filter(x => x.available_balance <= 0).map((row, idx) => (
                                                <tr key={`zero-${idx}`} className="opacity-40">
                                                    <td className="px-6 py-4 text-sm font-bold">{row.name}</td>
                                                    {activeTab === "sellers" && <td className="px-6 py-4 text-sm">{row.store_name}</td>}
                                                    <td className="px-6 py-4 text-sm">{row.phone}</td>
                                                    <td className="px-6 py-4 text-right font-medium">Pa gen rezèv (0 HTG)</td>
                                                    <td className="px-6 py-4 text-right">
                                                        <Button size="sm" variant="ghost" disabled className="text-xs uppercase font-bold tracking-wider">Peye</Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </EnterpriseLayout>
    );
};
